import request from '@oskecare/fe-utilities/lib/request';
import SessionIFrame from './SessionIFrame.js';

export const AUTH_INIT_SUCCESSFUL = 'auth-successful';
export const AUTH_INIT_FAILED = 'auth-failed';
export const AUTH_INIT_REDIRECTING = 'auth-redirecting';

/**
 * AuthManager module to handle the user authentication and authorization process
 *
 * @property {Logger} _logger
 * @property {?TagManager} _tagManager
 * @property {string} _monicaApiUrl
 * @property {string} _authServerUrl
 * @property {string} _partialLogoutUrl
 * @property {string} _clientId
 * @property {string} _userRole
 * @property {string} _customerAdminRole
 * @property {string} _basePath
 * @property {string} _idTokenCookieName
 * @property {boolean} _isSsoRedirectActivated
 * @property {?string} _idToken
 * @property {?Promise<Object>} _userData
 * @property {SessionIFrame} _sessionIFrame
 *
 * @see authentication docs: https://wiki.intranet.orange.sk/pages/viewpage.action?pageId=30617974
 */
export default class AuthManager {
  /**
   * @param {Logger} logger
   * @param {object} config
   */
  constructor({ logger, config }) {
    this._logger = logger;
    this._tagManager = null;

    this._monicaApiUrl = config.monicaApiUrl;
    this._authServerUrl = config.authServerUrl;
    this._partialLogoutUrl = config.partialLogoutUrl;
    this._clientId = config.clientId;
    this._userRole = config.userRole;
    this._customerAdminRole = config.customerAdminRole;
    this._basePath = config.basePath;
    this._idTokenCookieName = config.idTokenCookieName;

    this._isSsoRedirectActivated = false;
    this._idToken = null;
    this._userData = null;

    const refreshSsoUrl = new URL(this._authServerUrl);
    refreshSsoUrl.search = new URLSearchParams({
      response_type: 'code',
      client_id: this._clientId,
      scope: 'openid',
      redirect_uri: `${this._monicaApiUrl}/auth`,
      acr_values: this._userRole,
      promt: 'none',
    }).toString();
    this._sessionIFrame = new SessionIFrame(this._monicaApiUrl, refreshSsoUrl, logger, config);
  }

  /**
   * Initializes the AuthManager - fetches user data, identity list, and optionally sub-SNs.
   *
   * @param {?string} code - Login code from identity provider, that gets traded for access token
   * @returns {Promise<string>} - auth init status
   */
  async initialize(code = null) {
    await this._sessionIFrame.initialize(code);
    return this.login();
  }

  /**
   * @param {TagManager} tagManager
   */
  attachTagManager(tagManager) {
    this._tagManager = tagManager;
  }

  /**
   * Fetch user data and if the response is 401 - unauthorized then redirect to auth page.
   *
   * @return {Promise<string>} - auth init status
   */
  async login() {
    try {
      this._userData = await request({
        method: 'GET',
        route: `${this._monicaApiUrl}/userData`,
        logSource: 'infra-auth-manager',
        dataSource: null, // only unpacks token
        isFatal: true,
      });

      this._idToken = AuthManager.getIdToken(this._idTokenCookieName);
      return AUTH_INIT_SUCCESSFUL;
    } catch (error) {
      if (error.responseStatus === 401) {
        this._redirectToAuthPage();
        return AUTH_INIT_REDIRECTING;
      }
      await this._logger.logErrorObject(error);
      return AUTH_INIT_FAILED;
    }
  }

  /**
   * Calls userData endpoint to verify whether the user is logged in or not.
   * Used to prevent pointless retryLogin loops if an API falsely claims that user isn't logged in.
   *
   * @return {Promise<boolean>}
   */
  async verifyLogin() {
    try {
      await request({
        method: 'GET',
        route: `${this._monicaApiUrl}/userData`,
        logSource: 'infra-auth-manager',
        dataSource: null, // only unpacks token
      });
    } catch (error) {
      return false;
    }
    return true;
  }

  /**
   * Returns user data.
   *
   * @returns {object}
   */
  getUserData() {
    if (!this._userData) {
      throw new Error('Accessing AuthManager.getUserData before AuthManager.initialization.');
    }
    return this._userData;
  }

  /**
   * Retry login by redirecting to auth page
   */
  retryLogin() {
    this._redirectToAuthPage();
  }

  /**
   * Redirects to SSO authentication page with return url
   *
   * @private
   */
  _redirectToAuthPage() {
    const authServerUrl = new URL(this._authServerUrl);
    authServerUrl.search = new URLSearchParams({
      response_type: 'code',
      client_id: this._clientId,
      scope: 'user openid',
      state: this.getCurrentScreenPath(),
      acr_values: this._userRole,
    }).toString();

    // Redirect to Auth server
    if (!this._isSsoRedirectActivated) {
      this._isSsoRedirectActivated = true; // will prevent multiple calls in case multiple events was generated
      window.location.replace(authServerUrl);
    }
  }

  /**
   * Redirects to SSO authentication page with return params and role
   *
   * @param {string} returnUrl - url where to redirect after ID/RC was correctly entered
   */
  redirectToIdRcAuthPageWithReturnParams(returnUrl) {
    const authServerUrl = new URL(this._authServerUrl);
    authServerUrl.search = new URLSearchParams({
      response_type: 'code',
      client_id: this._clientId,
      scope: 'user openid',
      state: returnUrl,
      acr_values: this._customerAdminRole,
    }).toString();

    if (!this._isSsoRedirectActivated) {
      this._isSsoRedirectActivated = true; // will prevent multiple calls in case multiple events was generated

      // Redirect to Auth server
      window.location.assign(authServerUrl);
    }
  }

  /**
   * Redirects to auth API logout endpoint to remove HTTP only access_token cookie
   */
  logout() {
    this._tagManager?.mhubPush({
      event: 'action.logout.user',
    });
    // stop executing iframe token refresh functionality
    this._sessionIFrame.stop();
    window.location.assign(`${this._monicaApiUrl}/logout`);
  }

  /**
   * Redirects to SSO partial logout endpoint
   *
   * @param {string} identityId SSO identity ID
   */
  logoutPartially(identityId) {
    // get the latest version of token, as sid can be updated by refreshing iframe functionality
    this._idToken = AuthManager.getIdToken(this._idTokenCookieName);
    if (!this._idToken) {
      this._logger.error(
        'Expired or missing ID token during partial logout - retrying login.',
        'infra-auth-manager',
      );
      this.retryLogin();
      return;
    }

    const parsedIdToken = AuthManager.parseToken(this._idToken);

    // stop executing iframe token refresh functionality
    this._sessionIFrame.stop();

    const logoutUrl = new URL(this._partialLogoutUrl);
    logoutUrl.search = new URLSearchParams({
      state: identityId,
      sid: parsedIdToken['sid.orangeUser'],
      id_token_hint: this._idToken,
    }).toString();

    window.location.replace(logoutUrl);
  }

  /**
   * Redirects to SSO identity switch endpoint
   *
   * @param {string} identityId SSO identity ID
   */
  switchIdentity(identityId) {
    this._tagManager?.mhubPush({
      event: 'action.switch.identity',
    });

    // stop executing iframe token refresh functionality
    this._sessionIFrame.stop();

    const switchIdentityUrl = new URL(this._authServerUrl);
    switchIdentityUrl.search = new URLSearchParams({
      response_type: 'code',
      client_id: this._clientId,
      scope: 'user openid',
      acr_values: this._userRole,
      authnAttrs: `selectIdentity:${identityId}`,
    }).toString();

    window.location.replace(switchIdentityUrl);
  }

  /**
   * Get string representing the screen path of a certain URL, that is a relative path without the domain and without
   * the base application path.
   *
   * @param {URL} url
   */
  getScreenPath(url) {
    const basePath = this._basePath.replace(/\/?$/, ''); // remove potential trailing slash
    return url.href
      .replace(url.origin, '') // remove origin
      .replace(basePath, ''); // remove base path
  }

  /**
   * Get screen path of the current URL
   *
   * @see getScreenPath
   * @returns {string}
   */
  getCurrentScreenPath() {
    return this.getScreenPath(new URL(window.location));
  }

  /**
   * Pull ID token from it's cookie
   *
   * @param {string} idTokenCookieName - id_token cookie name
   * @returns {string|null}
   */
  static getIdToken(idTokenCookieName) {
    const cookie = document.cookie.match(`(^|;) ?${idTokenCookieName}=([^;]*)(;|$)`);
    return cookie ? cookie[2] : null;
  }

  /**
   * Parses JWT token
   * @see https://jwt.io/
   *
   * @param {string} token
   * @returns {object}
   */
  static parseToken(token) {
    const dataPart = token.split('.')[1];
    return JSON.parse(atob(dataPart));
  }

  /**
   * Used for authorizing submit order with ID code in products and services.
   * `authorized` param is appended to back url to prevent looping after unsuccessful SSO verification.
   *
   * @param {Object} additionalQueryParams
   */
  submitOrderRequestIdRc(additionalQueryParams) {
    const url = new URL(window.location);

    // avoid loop, if we already have param mode, we returned from SSO already
    if (url.searchParams.has('authorized')) {
      throw new Error(`SSO customerAdmin loop issue, multiple request for SSO ID/RC verification`);
    }

    const newParams = new URLSearchParams(url.searchParams);
    for (const [key, value] of Object.entries(additionalQueryParams)) {
      newParams.set(key, value);
    }

    newParams.set('authorized', 1);

    url.search = newParams.toString();

    this.redirectToIdRcAuthPageWithReturnParams(this.getScreenPath(url));
  }
}
